import { NamespacedStoreState } from '@Types/store.types';
import { createSelector } from '@reduxjs/toolkit';
import { moduleNamespace } from '@Store/config.store';
import { Component } from '@Types';
import {
	ComponentSourceLists,
	getMissingSourceKind,
	MissingSourceKind,
} from '@Utils/missingSourceKind';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

// A list only counts once it has resolved; until then it is undefined so
// nothing looks disconnected while loading or after a failed request.
const loadedOnly = <T>(list: T[], loaded?: boolean): T[] | undefined =>
	loaded ? list : undefined;

const ns = parseNamespace;

// Inputs are the individual lists and flags (not whole slices) so unrelated
// config edits don't rebuild the lookup and re-render every Category.
const lofListsSelector = createSelector(
	[
		(state: NamespacedStoreState) => ns(state).LoFConfig.componentFields,
		(state: NamespacedStoreState) =>
			ns(state).LoFConfig.componentFieldsLoaded,
		(state: NamespacedStoreState) =>
			ns(state).LoFConfig.componentInspectionFields,
		(state: NamespacedStoreState) =>
			ns(state).LoFConfig.componentInspectionFieldsLoaded,
		(state: NamespacedStoreState) =>
			!!ns(state).RiskStore.riskConfig?.riskSetting?.inspectionStandard,
	],
	(
		fields,
		fieldsLoaded,
		inspectionFields,
		inspectionFieldsLoaded,
		hasInspectionStandard,
	) => ({
		lofFields: loadedOnly(fields, fieldsLoaded),
		lofInspectionFields: loadedOnly(
			inspectionFields,
			inspectionFieldsLoaded,
		),
		hasInspectionStandard,
	}),
);

const cofFieldsSelector = createSelector(
	[
		(state: NamespacedStoreState) => ns(state).CoFConfig.componentFields,
		(state: NamespacedStoreState) =>
			ns(state).CoFConfig.componentFieldsLoaded,
	],
	(fields, loaded) => loadedOnly(fields, loaded),
);

const spatialListsSelector = createSelector(
	[
		(state: NamespacedStoreState) => ns(state).assets.spatialLayerList,
		(state: NamespacedStoreState) =>
			ns(state).assets.spatialLayerListLoaded,
		(state: NamespacedStoreState) => ns(state).assets.spatialProximityAttr,
		(state: NamespacedStoreState) =>
			ns(state).assets.spatialProximityAttrLoaded,
	],
	(layers, layersLoaded, attributes, attributesLoaded) => ({
		spatialLayers: loadedOnly(layers, layersLoaded),
		spatialAttributes: loadedOnly(attributes, attributesLoaded),
	}),
);

const runListsSelector = createSelector(
	[
		(state: NamespacedStoreState) => ns(state).RiskStore.taskData,
		(state: NamespacedStoreState) => ns(state).RiskStore.taskDataLoaded,
		(state: NamespacedStoreState) => ns(state).RiskStore.simulationData,
		(state: NamespacedStoreState) =>
			ns(state).RiskStore.simulationDataLoaded,
	],
	(taskData, taskDataLoaded, simulationData, simulationDataLoaded) => ({
		taskData: loadedOnly(taskData, taskDataLoaded),
		simulationData: loadedOnly(simulationData, simulationDataLoaded),
	}),
);

const sourceListsSelector = createSelector(
	[
		lofListsSelector,
		cofFieldsSelector,
		spatialListsSelector,
		runListsSelector,
	],
	(lof, cofFields, spatial, runs): ComponentSourceLists => ({
		...lof,
		cofFields,
		...spatial,
		...runs,
	}),
);

// Returns a lookup function, since "is this component's source missing" is
// asked per Category/Component row. Undefined means connected (or not yet
// known); see getMissingSourceKind.
export const selectMissingSourceKind = createSelector(
	sourceListsSelector,
	lists =>
		(
			forLoF: boolean,
			isCondition: boolean,
			component: Component,
		): MissingSourceKind | undefined =>
			getMissingSourceKind(component, forLoF, isCondition, lists),
);
